import { capitalize } from 'lodash'
import { TSelectOpt } from 'transfix-ui/components/Select/SelectTypes'
import { ICodeStatuses, ICodeTypes, IShipmentAccessorialNames } from 'types/graphqlTypes'
import { IUseUserDetailsState } from 'utils/hooks'
import { TAccessorialSchema } from '../components/AccessorialForm/schemas'
import { IPaymentTypes } from '../components/AccessorialForm/schemas/AccessorialSchema'

type TAccessorialSchemaKey = keyof TAccessorialSchema

export interface IGetDisabledFieldsOptions {
  userDetails?: IUseUserDetailsState | null
  /**
   * @example ICodeStatuses.active
   */
  codeStatus?: string
  /**
   * User Permission: Can change relay payments
   */
  changeRelayPayments?: boolean
  /**
   * Is feature flag enabled globally
   * @default false
   */
  canChangeAccessorialShipperChargeRelayPayments?: boolean
  /**
   * User Permission: Can change relay payments enabled accessorial's shipper charge
   */
  isMpRelayPaymentsEnabled?: boolean
  paymentType?: string | null
  codeType?: string | null | undefined
  isEditing?: boolean
}

export interface IAccessorialValues {
  label: string
  value: string
  enabled: boolean
  negativeAmount: boolean
  positiveAmount?: boolean
  getDisabledFields: (options: IGetDisabledFieldsOptions) => TAccessorialSchemaKey[]
}

const relayPaymentsDisabledFields = (
  options: IGetDisabledFieldsOptions,
  disabledFields: TAccessorialSchemaKey[]
) => {
  const {
    canChangeAccessorialShipperChargeRelayPayments = false,
    changeRelayPayments = false,
    codeStatus = '',
    codeType,
    isEditing,
    isMpRelayPaymentsEnabled = false,
    paymentType,
  } = options || {}

  /**
   * If global feature flag is disabled, return legacy fields
   */
  if (!isMpRelayPaymentsEnabled) {
    return disabledFields
  }

  const isPostChargeStatus = [ICodeStatuses.used, ICodeStatuses.processing].includes(
    codeStatus as ICodeStatuses
  )
  /**
   * the shipper charge field should be disabled...
   * If user does not have canChangeAccessorialShipperChargeRelayPayments permission and the paymentType is relay payments
   */

  if (isEditing && codeType === ICodeTypes.relay_code) {
    disabledFields.push('amount')
  }

  if (!canChangeAccessorialShipperChargeRelayPayments && paymentType === IPaymentTypes.relay) {
    disabledFields.push('shipperCharge', 'amount')
  }

  if (codeStatus === ICodeStatuses.active) {
    disabledFields.push('shipperFacilityUuid')
  }

  if (isPostChargeStatus) {
    disabledFields.push('paymentType', 'constraintAmount', 'shipperFacilityUuid', 'withComcheck')
  }

  /**
   * If user does not have changeRelayPayments permission, only show reimbursement radio button
   */
  if (!changeRelayPayments) {
    disabledFields.push('paymentType')
    return disabledFields
  }

  return disabledFields
}

export const ACCESSORIALS_CONFIG_BY_NAME: Record<IShipmentAccessorialNames, IAccessorialValues> = {
  bridge_toll: {
    label: 'Bridge Toll',
    value: 'Bridge Toll',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  circuity_fee: {
    value: 'Circuity Fee',
    label: 'Circuity',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  cross_dock: {
    value: 'Cross Dock',
    label: 'Cross Dock',
    enabled: true,
    getDisabledFields: options => {
      const disabledFields: TAccessorialSchemaKey[] = [
        'lumperServiceName',
        'escortServiceName',
        'trailerControlRecord',
      ]

      return relayPaymentsDisabledFields(options, disabledFields)
    },
    negativeAmount: false,
    positiveAmount: true,
  },
  late_fee: {
    value: 'Late Fee',
    label: 'Late Fee',
    enabled: true,
    getDisabledFields: options => {
      const disabledFields: TAccessorialSchemaKey[] = [
        'lumperServiceName',
        'escortServiceName',
        'trailerControlRecord',
      ]

      return relayPaymentsDisabledFields(options, disabledFields)
    },
    negativeAmount: true,
    positiveAmount: true,
  },
  restack: {
    value: 'Restack',
    label: 'Restack',
    enabled: true,
    getDisabledFields: options => {
      const disabledFields: TAccessorialSchemaKey[] = [
        'lumperServiceName',
        'escortServiceName',
        'trailerControlRecord',
      ]

      return relayPaymentsDisabledFields(options, disabledFields)
    },
    negativeAmount: false,
    positiveAmount: true,
  },
  deadhead_mileage: {
    value: 'Deadhead Mileage',
    label: 'Deadhead Mileage',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  deduction_fee: {
    value: 'Deduction Fee',
    label: 'Deduction',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'shipperCharge',
      'paymentType',
    ],
    negativeAmount: true,
    positiveAmount: false,
  },
  detention_fee: {
    value: 'Detention Fee',
    label: 'Detention',
    enabled: true,
    getDisabledFields: () => ['lumperServiceName', 'escortServiceName', 'paymentType'],
    negativeAmount: false,
    positiveAmount: true,
  },
  driver_assist: {
    value: 'Driver Assist',
    label: 'Driver Assist',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  driver_not_tracking_fee: {
    value: 'Driver Not Tracking Fee',
    label: 'Driver Not Tracking',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'shipperCharge',
      'paymentType',
    ],
    negativeAmount: true,
    positiveAmount: false,
  },
  es_escort_service_fee: {
    value: 'ES-Escort Service Fee',
    label: 'Escort',
    enabled: false,
    getDisabledFields: () => ['lumperServiceName', 'trailerControlRecord', 'paymentType'],
    negativeAmount: false,
    positiveAmount: true,
  },
  escort_fee: {
    value: 'Escort Fee',
    label: 'Escort',
    enabled: true,
    getDisabledFields: () => ['lumperServiceName', 'trailerControlRecord', 'paymentType'],
    negativeAmount: false,
    positiveAmount: true,
  },
  financial_reconciliation__for_ar_ap_use_only_: {
    value: 'Financial Reconciliation (For AR/AP Use Only)',
    label: 'Financial Reconciliation (For AR/AP Use Only)',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'trailerControlRecord',
      'escortServiceName',
      'paymentType',
    ],
    negativeAmount: true,
    positiveAmount: true,
  },
  flat_rate: {
    value: 'Flat Rate',
    label: 'Flat Rate',
    enabled: true,
    getDisabledFields: () => [
      'amount',
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  insurance_claims: {
    value: 'Insurance Claims',
    label: 'Insurance Claims',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: true,
    positiveAmount: false,
  },
  layover_fee: {
    value: 'Layover Fee',
    label: 'Layover',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  lumper_charge: {
    value: 'Lumper Charge',
    label: 'Lumper',
    enabled: true,
    negativeAmount: false,
    positiveAmount: true,
    getDisabledFields: options => {
      const disabledFields: TAccessorialSchemaKey[] = [
        'escortServiceName',
        'trailerControlRecord',
        'amountUsed',
      ]

      return relayPaymentsDisabledFields(options, disabledFields)
    },
  },
  miscellaneous: {
    value: 'Miscellaneous',
    label: 'Miscellaneous',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: true,
    positiveAmount: true,
  },
  otp_otd_late_fee: {
    value: 'OTP/OTD Late Fee',
    label: 'OTP/OTD',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'shipperCharge',
      'paymentType',
    ],
    negativeAmount: true,
    positiveAmount: false,
  },
  out_of_route_miles_fee: {
    value: 'Out of Route Miles Fee',
    label: 'Out of Route Miles',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  preloading_charge: {
    value: 'Preloading Charge',
    label: 'Preloading',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  reconsignment_fee: {
    value: 'Reconsignment Fee',
    label: 'Reconsignment',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  stop_off_charge: {
    value: 'Stop-off Charge',
    label: 'Stop-off',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  storage_fee: {
    value: 'Storage Fee',
    label: 'Storage',
    enabled: true,
    getDisabledFields: options => {
      const disabledFields: TAccessorialSchemaKey[] = [
        'lumperServiceName',
        'escortServiceName',
        'trailerControlRecord',
      ]

      return relayPaymentsDisabledFields(options, disabledFields)
    },
    negativeAmount: false,
    positiveAmount: true,
  },
  team_driver: {
    value: 'Team Driver',
    label: 'Team Driver',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  tier_1: {
    value: 'Tier 1',
    label: 'Tier 1',
    enabled: true,
    getDisabledFields: () => [
      'amount',
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  tonu: {
    value: 'TONU',
    label: 'TONU',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  trailer_detention_fee: {
    value: 'Trailer Detention Fee',
    label: 'Trailer Detention',
    enabled: true,
    getDisabledFields: () => ['lumperServiceName', 'escortServiceName', 'paymentType'],
    negativeAmount: false,
    positiveAmount: true,
  },
  up_charge: {
    value: 'Up Charge',
    label: 'Up Charge',
    enabled: true,
    getDisabledFields: () => [
      'amount',
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  driver_loading_unloading: {
    value: 'Driver Loading/Unloading',
    label: 'Driver Loading/Unloading',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  keep_from_freezing: {
    value: 'Keep from Freezing',
    label: 'Keep from Freezing',
    enabled: true,
    getDisabledFields: () => [
      'amount',
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  redelivery___refused_goods: {
    value: 'Redelivery / Refused Goods',
    label: 'Redelivery / Refused Goods',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
  returned_load: {
    value: 'Returned Load',
    label: 'Returned Load',
    enabled: true,
    getDisabledFields: () => [
      'lumperServiceName',
      'escortServiceName',
      'trailerControlRecord',
      'paymentType',
    ],
    negativeAmount: false,
    positiveAmount: true,
  },
}

export const OopReasonOptions: TSelectOpt[] = [
  {
    value: 'AM',
    label: 'AM',
    description:
      'Expense is being paid out due to AM-related mishaps (ex. bad quoting, incorrect load build, etc.), or to honor AM approval.',
  },
  {
    value: 'CM',
    label: 'CM',
    description:
      'Expense is being paid out to CM/Broker-related mishaps (ex. Failure to communicate to the carrier, not responding to Stark tags of issue updates, etc.), or to honor CM approval.',
  },
  {
    value: 'Carrier Relationship',
    label: 'Carrier Relationship',
    description:
      'Expense is being paid to maintain a carrier relationship or due to carrier-faulted issues. Approved by a member or leader of the Carrier team.',
  },
  {
    value: 'Cross-dock or Restack',
    label: 'Cross-dock or Restack',
    description:
      'Expense is being paid to fund cross-dock, restack, etc. while we attempt to secure a new delivery appointment.',
  },
  {
    value: 'Customer Rejected',
    label: 'Customer Rejected',
    description:
      'Submitted accessorial request rejected by the customer due to incorrect information or request did not meet requirements for approval.',
  },
  {
    value: 'Late Assignment',
    label: 'Late Assignment',
    description:
      'Expense is being paid out due to sending the carrier in late for an appointment, late recovery from a bounce, shipment being put on the board late, etc. Approved by a member or leader of the Carrier team.',
  },
  {
    value: 'Scheduling',
    label: 'Scheduling',
    description:
      'Expense is being paid out because incorrect appointment times were added to the shipment.',
  },
  {
    value: 'Timely Submission',
    label: 'Timely Submission',
    description:
      'Expense is being paid out though there was a failure to provide information within the timeline provided by the customer.',
  },
  {
    value: 'Track & Trace',
    label: 'Track & Trace',
    description: 'Expense is being paid out due to misalignment of carrier communication.',
  },
]

export const OopOwnerRoleDefaults = [
  {
    oopReason: 'AM',
    ownerRole: 'Account Manager',
  },
  {
    oopReason: 'CM',
    ownerRole: 'Carrier Manager',
  },
  {
    oopReason: 'Carrier Relationship',
    ownerRole: 'Carrier Manager',
  },
  {
    oopReason: 'Cross-dock or Restack',
    ownerRole: 'Carrier Manager',
  },
  {
    oopReason: 'Customer Rejected',
    ownerRole: 'Account Manager',
  },
  {
    oopReason: 'Late Assignment',
    ownerRole: 'Carrier Manager',
  },
  {
    oopReason: 'Scheduling',
    ownerRole: 'Scheduling Rep',
  },
  {
    oopReason: 'Timely Submission',
    ownerRole: 'Carrier Manager',
  },
  {
    oopReason: 'Track & Trace',
    ownerRole: 'Carrier Manager',
  },
]

export const ACCESSORIALS_SHIPPER_APPROVAL_STATES = ['pending', 'approved', 'rejected']

export const AccessorialShipperApprovalStateOptions: TSelectOpt[] = ACCESSORIALS_SHIPPER_APPROVAL_STATES.map(
  key => ({
    value: key,
    label: capitalize(key),
  })
)
